import React, { useCallback, useMemo, useState } from 'react';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { useLoader, useSubmitter } from '../../platform/Hook';
import { Box, Button, Prompt, TypeaheadInput, TypeaheadItem } from '../../ui';
import { LoadingGuard } from '../../ui/LoadingGuard';
import { PillEditor, PillableItem } from '../../ui/PillEditor';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { AppEvents } from '../AppEvents';
import { useResolve } from '../Container';
import { Copy, Title } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';

const emailPattern = /.*@.*\..*/;

const toPillables = (item: TypeaheadItem) => {
    if (item.id.includes('@')) {
        const emails = item.id.split(/[\s\,\;]/g).filter(x => !!x && !!x.match(emailPattern)).map(x => x.toLowerCase());
        return emails.map(x => PillableItem(x, x, 'deqo://icons/hero/mail'));
    }
    else {
        return [PillableItem(item.id, item.label)];
    }
};

export const WorkspaceInvitePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { workspaceId } = args;

    const api = useResolve(ApiClient);
    const evts = useResolve(AppEvents);

    // const [working, setWorking] = useState(false);
    const [selection, setSelection] = useState([] as PillableItem[]);

    const [loading, data] = useLoader(async () => {
        const ws = await api.exec(v2.workspaces.infoLite(workspaceId));
        const ml = await api.exec(v2.workspaces.members.list(workspaceId));
        return [ws, ml] as const;
    }, []);

    const [workspace, members] = data ?? [null, []];
    const existingLookup = useMemo(() => new Set(([...members, ...selection]).map(x => x.id)), [members, selection]);

    const userSource = useCallback(async (query: string) => {
        if (query.includes('@')) {
            query = query.toLowerCase().trim();
            if (query.match(emailPattern)) {
                return Promise.resolve([
                    TypeaheadItem(query, `Invite ${query}`, existingLookup.has(query) ? 'Already Added' : null, existingLookup.has(query))
                ]);
            }
            else {
                return Promise.resolve([TypeaheadItem('none', 'Type a full email address...', null, true)]);
            }
        }
        else {
            const users = await api.exec(v2.users.list({ like: query, count: 8 }));
            if (users.length) {
                return users.map(x =>
                    TypeaheadItem(x.id, x.name, existingLookup.has(x.id) ? 'Already Added' : null, existingLookup.has(x.id))
                );
            }
            else {
                return Promise.resolve([TypeaheadItem('none', 'No matches, try an email instead.', null, true)]);
            }
        }
    }, [existingLookup]);

    const onSelect = (tm: TypeaheadItem) => {
        setSelection(x => x.concat(toPillables(tm)).slice(0, 50));
        return true;
    };

    const [onInvite, working, result] = useSubmitter(async () => {
        const values = selection.map(x => x.id);
        const results = await api.exec(v2.workspaces.members.add({ workspaceId, values }));
        for (const user of results) {
            evts.push('workspace:members:add', { workspace, user });
        }
        action(true);
    }, [selection]);

    const onPromptAction = useCallback((confirm: boolean) => {
        if (!confirm) dismiss();
        else onInvite();
    }, []);

    return <Prompt ignoreEnter onAction={working ? null : onPromptAction}>
        <Popup w="36">
            <Popup.Close onAction={() => onPromptAction(false)} />
            <Popup.Title>Invite people to {workspace?.label}</Popup.Title>
            <Popup.Content>
                <Box minHeight="8">
                    <LoadingGuard loading={loading}>
                        <Copy>
                            Search for teammates if they&apos;re already in the account, or add their emails to send them an invite.
                        </Copy>
                        <TypeaheadInput
                            autoFocus
                            placeholder="Enter a name or email address"
                            typeaheadSource={userSource}
                            onTypeaheadSelect={onSelect}
                        />
                        <Box margin="1 -.25">
                            <PillEditor items={selection} onUpdate={setSelection} config={{
                                canEdit: false,
                                canRemove: true,
                                canAdd: false,
                                canSort: false,
                            }} />
                        </Box>
                    </LoadingGuard>
                    {selection.length >= 50 && <Title size="sm">Maximum 50 invites at a time.</Title>}
                    <ResultInfoForSubmitter result={result} />
                </Box>
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={working || !selection.length} variant="primary" onAction={onInvite}>
                    {working ? 'Inviting...' : `Invite ${!!selection.length ? `(${selection.length})` : ''}`}
                </Button>
                <Button disabled={working} onAction={dismiss}>
                    Close
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};
